import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import API_URL from '../../config/api';
import { showToast } from '../../utils/toast';
import { portalInviteButtonLabel, portalStatusBadge, resolveTenantPortalStatus } from '../../utils/tenantPortalStatus';
import { getTenantPortalLoginUrl, getTenantPortalLabel } from '../../utils/tenantPortalUrl';
import {
  UK, TENANT_FILTERS, fmt, ini, effectiveStatus, tenantAccent, rtrBadge, leaseLine,
} from './tenantFormShared';

const PAGE_SIZE = 15;

function TenantAvatar({ t, size = 40 }: { t: any; size?: number }) {
  if (t.image_url) return <img src={t.image_url} alt="" style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', border: '2px solid #e5e7eb', flexShrink: 0 }} />;
  const colors = ['#012169', '#16a34a', '#E6A817', '#C8102E', '#0d9488'];
  const bg = colors[(t.full_name?.charCodeAt(0) || 0) % colors.length];
  return <div style={{ width: size, height: size, borderRadius: '50%', background: bg, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: Math.floor(size * 0.35), flexShrink: 0 }}>{ini(t.full_name)}</div>;
}

export default function Tenants() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [filterTab, setFilterTab] = useState<string>('All');
  const [page, setPage] = useState(1);
  const [showArchived, setShowArchived] = useState(false);
  const [archivedTenants, setArchivedTenants] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [inviting, setInviting] = useState<number | null>(null);
  const [archiving, setArchiving] = useState<number | null>(null);

  const [statementId, setStatementId] = useState<number | null>(null);
  const [statementData, setStatementData] = useState<any | null>(null);
  const [statementLoading, setStatementLoading] = useState(false);
  const [emailingStatement, setEmailingStatement] = useState(false);
  const [selectedHistoricalLeaseId, setSelectedHistoricalLeaseId] = useState<number | null>(null);
  const [allPropertiesMode, setAllPropertiesMode] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/landlord/tenants`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      setTenants(d.tenants || []);
    } catch { setError('Failed to load tenants'); }
    finally { setLoading(false); }
  }, [token]);

  const loadArchived = useCallback(async () => {
    if (!token) return;
    const r = await fetch(`${API_URL}/landlord/tenants?archived=true`, { headers: { Authorization: `Bearer ${token}` } });
    const d = await r.json();
    setArchivedTenants(d.tenants || []);
  }, [token]);

  useEffect(() => { load(); loadArchived(); }, [load, loadArchived]);
  useEffect(() => { setPage(1); }, [search, filterTab]);

  const activeTenants = tenants.filter((t: any) => !t.archived);
  const activeCount = activeTenants.filter((t: any) => effectiveStatus(t) === 'active').length;
  const noRtr = activeTenants.filter((t: any) => effectiveStatus(t) === 'active' && !t.right_to_rent_checked).length;
  const rtrExpiring = activeTenants.filter((t: any) => {
    if (effectiveStatus(t) !== 'active' || !t.right_to_rent_expiry) return false;
    const days = Math.floor((new Date(t.right_to_rent_expiry).getTime() - Date.now()) / 86400000);
    return days >= 0 && days <= 30;
  }).length;

  const filtered = activeTenants.filter((t: any) => {
    const q = search.toLowerCase().trim();
    const leaseSearch = (t.active_leases || []).some((l: any) =>
      (l.property_address || '').toLowerCase().includes(q) || (l.room_number || '').toLowerCase().includes(q));
    const matchesSearch = !q || (t.full_name || '').toLowerCase().includes(q) || (t.email || '').toLowerCase().includes(q)
      || (t.phone || '').toLowerCase().includes(q) || (t.property_address || '').toLowerCase().includes(q)
      || (t.room_number || '').toLowerCase().includes(q) || leaseSearch;
    if (!matchesSearch) return false;
    if (filterTab === 'Active') return effectiveStatus(t) === 'active';
    if (filterTab === 'No Active Lease') return effectiveStatus(t) === 'former';
    if (filterTab === 'Multi-Property') return (t.active_lease_count || 0) > 1;
    if (filterTab === 'No RTR') return effectiveStatus(t) === 'active' && !t.right_to_rent_checked;
    if (filterTab === 'RTR Expiring') {
      if (effectiveStatus(t) !== 'active' || !t.right_to_rent_expiry) return false;
      const days = Math.floor((new Date(t.right_to_rent_expiry).getTime() - Date.now()) / 86400000);
      return days >= 0 && days <= 30;
    }
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const filterCount = (f: string) => {
    if (f === 'Active') return activeCount;
    if (f === 'No Active Lease') return activeTenants.filter((t: any) => effectiveStatus(t) === 'former').length;
    if (f === 'Multi-Property') return activeTenants.filter((t: any) => (t.active_lease_count || 0) > 1).length;
    if (f === 'No RTR') return noRtr;
    if (f === 'RTR Expiring') return rtrExpiring;
    return 0;
  };

  const inviteToPortal = async (t: any) => {
    if (!t.email) { showToast('Add an email address first'); return; }
    const status = resolveTenantPortalStatus(t);
    const msg = status === 'invited' || status === 'invite_expired'
      ? `Resend portal invite to ${t.email}?`
      : `Send portal invite to ${t.email}?`;
    if (!window.confirm(msg)) return;
    setInviting(t.id);
    try {
      const res = await fetch(`${API_URL}/landlord/tenants/${t.id}/invite-portal`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      showToast(res.ok ? `Invite sent to ${t.email}` : data.error || 'Failed');
      if (res.ok) load();
    } catch { showToast('Network error'); }
    finally { setInviting(null); }
  };

  const archiveTenant = async (t: any) => {
    if (!window.confirm(`Archive ${t.full_name}?`)) return;
    setArchiving(t.id);
    await fetch(`${API_URL}/landlord/tenants/${t.id}/archive`, { method: 'PATCH', headers: { Authorization: `Bearer ${token}` } });
    load(); loadArchived(); setArchiving(null);
  };

  const restoreTenant = async (id: number) => {
    await fetch(`${API_URL}/landlord/tenants/${id}/restore`, { method: 'PATCH', headers: { Authorization: `Bearer ${token}` } });
    load(); loadArchived();
  };

  const openStatement = (t: any) => {
    loadStatement(t.id, undefined, false);
  };

  const loadStatement = async (id: number, leaseId?: number, allProperties = false) => {
    setStatementId(id); setStatementLoading(true); setStatementData(null);
    setAllPropertiesMode(allProperties);
    setSelectedHistoricalLeaseId(allProperties ? null : (leaseId ?? null));
    const url = allProperties ? `${API_URL}/landlord/tenants/${id}/statement?all_properties=true`
      : leaseId ? `${API_URL}/landlord/tenants/${id}/statement?lease_id=${leaseId}`
      : `${API_URL}/landlord/tenants/${id}/statement`;
    const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (r.ok) {
      const data = await r.json();
      setStatementData(data);
      if (allProperties) setSelectedHistoricalLeaseId(null);
      else if (leaseId) setSelectedHistoricalLeaseId(leaseId);
      else if (data.statement_period?.lease_id) setSelectedHistoricalLeaseId(data.statement_period.lease_id);
      else setSelectedHistoricalLeaseId(null);
    }
    setStatementLoading(false);
  };

  const emailStatement = async (id: number) => {
    setEmailingStatement(true);
    const body: any = {};
    if (allPropertiesMode) body.all_properties = true;
    if (selectedHistoricalLeaseId) body.lease_id = selectedHistoricalLeaseId;
    const r = await fetch(`${API_URL}/landlord/tenants/${id}/statement/email`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
    const d = await r.json();
    showToast(r.ok ? `Statement sent to ${d.sent_to}` : d.error || 'Failed');
    setEmailingStatement(false);
  };

  const downloadStatementPDF = async (id: number, name: string) => {
    const suffix = allPropertiesMode ? '?all_properties=true' : selectedHistoricalLeaseId ? `?lease_id=${selectedHistoricalLeaseId}` : '';
    const r = await fetch(`${API_URL}/landlord/tenants/${id}/statement/pdf${suffix}`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `statement-${name.replace(/\s+/g, '-')}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };

  const portalUrl = getTenantPortalLoginUrl();

  const copyPortalLink = () => {
    navigator.clipboard?.writeText(portalUrl);
    showToast('Tenant portal link copied');
  };

  const CSS = `
    .tn-page { padding: 24px; max-width: 1400px; margin: 0 auto; font-family: 'DM Sans', system-ui, sans-serif; background: #eef2f6; min-height: 100vh; }
    .tn-banner-wrap { margin: -24px -24px 28px; padding: 10px 10px 14px; background: linear-gradient(145deg, #064e3b 0%, #0d9488 38%, #0369a1 72%, #1e40af 100%); border-radius: 0 0 22px 22px; box-shadow: 0 12px 40px rgba(1,33,105,0.22); }
    .tn-banner-glass { display: flex; align-items: center; justify-content: space-between; gap: 20px; flex-wrap: wrap; background: rgba(255,255,255,0.11); backdrop-filter: blur(22px); border: 1px solid rgba(255,255,255,0.28); border-radius: 18px; padding: 34px 38px; box-shadow: 0 8px 32px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.35); }
    .tn-banner-glass h1 { margin: 0; font-size: 34px; font-weight: 800; color: #fff; letter-spacing: -0.03em; }
    .tn-banner-glass p { margin: 8px 0 0; font-size: 14px; color: rgba(255,255,255,0.88); }
    .tn-btn-glass { background: rgba(255,255,255,0.14); color: #fff; border: 1px solid rgba(255,255,255,0.35); border-radius: 10px; padding: 11px 20px; font-weight: 700; font-size: 13px; cursor: pointer; }
    .tn-btn-add { background: #fff; color: ${UK.blue}; border: none; border-radius: 10px; padding: 11px 24px; font-weight: 800; font-size: 14px; cursor: pointer; box-shadow: 0 4px 16px rgba(0,0,0,0.14); }
    .tn-kpi-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 24px; }
    .tn-kpi { border-radius: 16px; padding: 26px 24px 22px; min-height: 118px; }
    .tn-kpi-white { background: ${UK.white}; border: 1.5px solid #e2e8f0; box-shadow: 0 8px 24px rgba(0,0,0,0.06); }
    .tn-kpi-blue { background: linear-gradient(145deg, ${UK.blue}, #1e3a8a); box-shadow: 0 10px 28px rgba(1,33,105,0.35); }
    .tn-kpi-red { background: linear-gradient(145deg, ${UK.red}, #ef4444); box-shadow: 0 10px 28px rgba(200,16,46,0.32); }
    .tn-kpi-gold { background: linear-gradient(145deg, #ca8a04, ${UK.gold}); box-shadow: 0 10px 28px rgba(230,168,23,0.35); }
    .tn-kpi .kl { font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.09em; margin-bottom: 8px; }
    .tn-kpi-white .kl { color: #64748b; } .tn-kpi-blue .kl, .tn-kpi-red .kl, .tn-kpi-gold .kl { color: rgba(255,255,255,0.85); }
    .tn-kpi .kv { font-size: 32px; font-weight: 800; line-height: 1.1; font-family: 'DM Mono', monospace; }
    .tn-kpi-white .kv { color: ${UK.blue}; } .tn-kpi-blue .kv, .tn-kpi-red .kv, .tn-kpi-gold .kv { color: #fff; }
    .tn-kpi .ks { font-size: 12px; margin-top: 6px; }
    .tn-kpi-white .ks { color: #94a3b8; } .tn-kpi-blue .ks, .tn-kpi-red .ks, .tn-kpi-gold .ks { color: rgba(255,255,255,0.75); }
    .tn-toolbar { display: flex; gap: 12px; margin-bottom: 20px; flex-wrap: wrap; align-items: center; }
    .tn-search { flex: 1; min-width: 220px; max-width: 420px; display: flex; padding: 10px 16px; background: #fff; border: 1.5px solid #e2e8f0; border-radius: 10px; }
    .tn-search input { border: none; outline: none; width: 100%; font-size: 14px; }
    .tn-filter { padding: 10px 36px 10px 14px; border-radius: 10px; border: 1.5px solid #e2e8f0; background: #fff; font-size: 13px; font-weight: 700; color: ${UK.blue}; appearance: none; min-width: 200px; cursor: pointer; }
    .tn-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(340px, 1fr)); gap: 16px; }
    .tn-card { background: #fff; border-radius: 12px; border: 1px solid #e5e7eb; overflow: hidden; box-shadow: 0 2px 10px rgba(0,0,0,0.04); }
    .tn-accent { height: 4px; }
    .tn-card-body { padding: 16px 18px 12px; }
    .tn-card-foot { display: grid; grid-template-columns: repeat(5, 1fr); gap: 6px; padding: 10px 14px 14px; border-top: 1px solid #f1f5f9; }
    .tn-act { padding: 8px 6px; border-radius: 8px; font-size: 11px; font-weight: 700; cursor: pointer; border: none; text-align: center; }
    .tn-act-pink { background: linear-gradient(145deg, #db2777, #ec4899); color: #fff; }
    .tn-act-gold { background: linear-gradient(145deg, #ca8a04, ${UK.gold}); color: #fff; }
    .tn-act-blue { background: ${UK.blue}; color: #fff; }
    .tn-act-white { background: #fff; color: ${UK.blue}; border: 1.5px solid ${UK.blue}; }
    .tn-act-red { background: ${UK.red}; color: #fff; }
    .tn-pager { display: flex; align-items: center; justify-content: center; gap: 12px; margin-top: 20px; }
    .tn-pager button { padding: 8px 16px; border-radius: 8px; border: 1px solid #d1d5db; background: #fff; font-weight: 700; cursor: pointer; }
    .tn-pager button:disabled { opacity: 0.4; cursor: default; }
    .tn-portal-strip { display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap; background: linear-gradient(135deg, #fdf2f8 0%, #fce7f3 100%); border: 1.5px solid #f9a8d4; border-radius: 12px; padding: 14px 18px; margin-bottom: 20px; }
    .tn-portal-url { font-family: 'DM Mono', monospace; font-size: 13px; color: ${UK.blue}; font-weight: 600; }
    .tn-portal-btn { background: linear-gradient(145deg, #db2777, #ec4899); color: #fff; border: none; border-radius: 8px; padding: 9px 16px; font-weight: 700; font-size: 12px; cursor: pointer; }
    .tn-portal-btn-outline { background: #fff; color: #9d174d; border: 1px solid #f9a8d4; border-radius: 8px; padding: 9px 16px; font-weight: 700; font-size: 12px; cursor: pointer; text-decoration: none; }
    @media (max-width: 900px) { .tn-kpi-row { grid-template-columns: repeat(2, 1fr); } .tn-card-foot { grid-template-columns: repeat(3, 1fr); } }
  `;

  return (
    <div className="tn-page">
      <style>{CSS}</style>

      <div className="tn-banner-wrap">
        <div className="tn-banner-glass">
          <div>
            <h1>Tenants</h1>
            <p>{activeTenants.length} tenant{activeTenants.length !== 1 ? 's' : ''} · {activeCount} with active leases</p>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" className="tn-btn-glass" onClick={() => setShowArchived(a => !a)}>
              {showArchived ? 'Hide Archived' : `Archived${archivedTenants.length ? ` (${archivedTenants.length})` : ''}`}
            </button>
            <button type="button" className="tn-btn-add" onClick={() => navigate('/landlord/tenants/new')}>+ Add Tenant</button>
          </div>
        </div>
      </div>

      <div className="tn-portal-strip">
        <div>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#9d174d', marginBottom: 4 }}>Tenant portal</div>
          <div className="tn-portal-url">{getTenantPortalLabel()}</div>
          <div style={{ fontSize: 12, color: '#831843', marginTop: 4 }}>Share with tenants — rent, maintenance & documents in one place</div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" className="tn-portal-btn" onClick={copyPortalLink}>Copy link</button>
          <a href={portalUrl} target="_blank" rel="noopener noreferrer" className="tn-portal-btn-outline">Open portal ↗</a>
        </div>
      </div>

      <div className="tn-kpi-row">
        <div className="tn-kpi tn-kpi-white">
          <div className="kl">Total Tenants</div>
          <div className="kv">{activeTenants.length}</div>
          <div className="ks">in your records</div>
        </div>
        <div className="tn-kpi tn-kpi-blue">
          <div className="kl">Active Leases</div>
          <div className="kv">{activeCount}</div>
          <div className="ks">currently letting</div>
        </div>
        <div className="tn-kpi tn-kpi-red">
          <div className="kl">RTR Missing</div>
          <div className="kv">{noRtr}</div>
          <div className="ks">{noRtr > 0 ? 'checks needed' : 'all checked'}</div>
        </div>
        <div className="tn-kpi tn-kpi-gold">
          <div className="kl">RTR Expiring</div>
          <div className="kv">{rtrExpiring}</div>
          <div className="ks">within 30 days</div>
        </div>
      </div>

      <div className="tn-toolbar">
        <div className="tn-search">
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, email, property, room…" />
        </div>
        <select className="tn-filter" value={filterTab} onChange={e => setFilterTab(e.target.value)}>
          {TENANT_FILTERS.map(f => (
            <option key={f} value={f}>{f}{filterCount(f) > 0 && f !== 'All' ? ` (${filterCount(f)})` : ''}</option>
          ))}
        </select>
      </div>

      {error && <div style={{ background: '#fee2e2', color: UK.red, padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontWeight: 600 }}>{error}</div>}

      {noRtr > 0 && (
        <div style={{ background: '#fef9c3', border: '1px solid #fde047', borderRadius: 10, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#713f12' }}>
          <strong>Right to Rent checks outstanding</strong> — UK law requires verification for every tenant in England.
        </div>
      )}

      {loading ? (
        <p style={{ textAlign: 'center', padding: 60, color: '#9ca3af' }}>Loading tenants…</p>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, background: '#fff', borderRadius: 12, border: '2px dashed #e5e7eb' }}>
          <div style={{ fontWeight: 700, color: '#374151' }}>{activeTenants.length === 0 ? 'No tenants yet' : 'No matching tenants'}</div>
          {activeTenants.length === 0 && (
            <button type="button" onClick={() => navigate('/landlord/tenants/new')} style={{ marginTop: 16, background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 22px', fontWeight: 700, cursor: 'pointer' }}>+ Add Tenant</button>
          )}
        </div>
      ) : (
        <>
          <div className="tn-grid">
            {pageItems.map((t: any) => {
              const accent = tenantAccent(t);
              const rb = rtrBadge(t);
              const leases = t.active_leases || [];
              const multi = (t.active_lease_count || 0) > 1;
              const displayRent = multi ? t.total_active_rent : t.active_rent;
              return (
                <div key={t.id} className="tn-card">
                  <div className="tn-accent" style={{ background: accent.color }} />
                  <div className="tn-card-body">
                    <div style={{ display: 'flex', gap: 12, marginBottom: 10 }}>
                      <TenantAvatar t={t} size={44} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 800, fontSize: 15, color: '#111' }}>{t.full_name}</div>
                        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{t.email}{t.phone ? ` · ${t.phone}` : ''}</div>
                        <span style={{ display: 'inline-block', marginTop: 6, fontSize: 10, fontWeight: 800, padding: '3px 8px', borderRadius: 5, background: accent.color + '18', color: accent.color }}>{accent.label}</span>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 18, fontWeight: 800, color: UK.blue, fontFamily: 'DM Mono, monospace' }}>
                          {displayRent ? `£${Number(displayRent).toLocaleString()}` : '—'}
                        </div>
                        <div style={{ fontSize: 10, color: '#9ca3af' }}>{displayRent ? '/mo' : 'no rent'}</div>
                      </div>
                    </div>
                    <div style={{ fontSize: 12, color: '#374151', marginBottom: 8 }}>
                      {leases.length > 0 ? leases.slice(0, 2).map((l: any) => (
                        <div key={l.id} style={{ marginBottom: 3 }}>{leaseLine(l)}</div>
                      )) : (t.property_address || 'No property linked')}
                      {leases.length > 2 && <div style={{ color: '#9ca3af' }}>+{leases.length - 2} more leases</div>}
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 5, background: rb.bg, color: rb.color }}>{rb.label}</span>
                      {(() => {
                        const pb = portalStatusBadge(resolveTenantPortalStatus(t));
                        return <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 5, background: pb.bg, color: pb.color }}>{pb.label}</span>;
                      })()}
                    </div>
                    {effectiveStatus(t) === 'active' && (
                      <button type="button" onClick={() => navigate('/landlord/leases')} style={{ marginTop: 10, background: 'none', border: 'none', color: '#16a34a', fontSize: 11, fontWeight: 700, cursor: 'pointer', padding: 0 }}>+ Add lease in Leases →</button>
                    )}
                  </div>
                  <div className="tn-card-foot">
                    <button type="button" className="tn-act tn-act-pink" onClick={() => navigate(`/landlord/tenants/${t.id}`)}>View</button>
                    <button type="button" className="tn-act tn-act-gold" onClick={() => openStatement(t)}>Statement</button>
                    <button type="button" className="tn-act tn-act-blue" onClick={() => inviteToPortal(t)} disabled={inviting === t.id || resolveTenantPortalStatus(t) === 'active'}>{portalInviteButtonLabel(resolveTenantPortalStatus(t), inviting === t.id)}</button>
                    <button type="button" className="tn-act tn-act-white" onClick={() => navigate(`/landlord/tenants/${t.id}/edit`)}>Edit</button>
                    <button type="button" className="tn-act tn-act-red" onClick={() => archiveTenant(t)} disabled={archiving === t.id}>{archiving === t.id ? '…' : 'Archive'}</button>
                  </div>
                </div>
              );
            })}
          </div>
          {totalPages > 1 && (
            <div className="tn-pager">
              <button type="button" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#64748b' }}>Page {page} of {totalPages} · {filtered.length} tenants</span>
              <button type="button" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next →</button>
            </div>
          )}
        </>
      )}

      {showArchived && (
        <div style={{ marginTop: 36, borderTop: '2px dashed #e2e8f0', paddingTop: 24 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 14 }}>Archived Tenants ({archivedTenants.length})</div>
          {archivedTenants.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8', background: '#fff', borderRadius: 12 }}>No archived tenants</div>
          ) : (
            <div className="tn-grid">
              {archivedTenants.map((t: any) => (
                <div key={t.id} className="tn-card">
                  <div className="tn-accent" style={{ background: '#94a3b8' }} />
                  <div className="tn-card-body" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <TenantAvatar t={t} size={36} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700 }}>{t.full_name}</div>
                      <div style={{ fontSize: 12, color: '#9ca3af' }}>{t.property_address || t.email}</div>
                    </div>
                    <button type="button" onClick={() => restoreTenant(t.id)} style={{ padding: '7px 14px', background: UK.blue, color: '#fff', border: 'none', borderRadius: 7, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>Restore</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {statementId !== null && (() => {
        const st = statementData?.tenant;
        const statementModeAll = allPropertiesMode || statementData?.statement_period?.mode === 'all_properties';
        const statementProperty = statementModeAll
          ? 'All linked properties'
          : (statementData?.statement_period?.property_address || st?.property_address || '—');
        const ml = (d: string) => new Date(d).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
        const closeStatement = () => { setStatementId(null); setSelectedHistoricalLeaseId(null); setAllPropertiesMode(false); };
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 20px', overflowY: 'auto' }} onClick={closeStatement}>
            <div style={{ background: '#fff', borderRadius: 14, padding: 28, maxWidth: 680, width: '100%' }} onClick={e => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800 }}>Rent Statement</h2>
                <button type="button" onClick={closeStatement} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#6b7280', lineHeight: 1 }}>×</button>
              </div>
              {statementLoading ? (
                <p style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>Loading…</p>
              ) : statementData && (
                <>
                  <div style={{ display: 'flex', gap: 14, alignItems: 'center', background: '#f8fafc', borderRadius: 10, padding: '14px 16px', marginBottom: 20 }}>
                    <TenantAvatar t={st} size={48} />
                    <div>
                      <div style={{ fontWeight: 800, fontSize: 16, color: '#0f172a' }}>{st?.full_name}</div>
                      <div style={{ color: '#6b7280', fontSize: 13 }}>{statementProperty}{!statementModeAll && st?.room_number ? ` — ${st.room_number}` : ''}</div>
                      {statementData.statement_period?.monthly_rent && !statementModeAll && (
                        <div style={{ fontSize: 12, color: '#0d9488', marginTop: 2 }}>
                          £{Number(statementData.statement_period.monthly_rent).toLocaleString()}/mo · from {fmt(statementData.statement_period.start_date)}
                        </div>
                      )}
                      {statementData.statement_period && (
                        <div style={{ fontSize: 12, color: '#374151', marginTop: 4, fontWeight: 700 }}>
                          Statement period: {fmt(statementData.statement_period.start_date)} → {fmt(statementData.statement_period.end_date)}{statementModeAll ? ' · all properties' : ''}
                        </div>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                    <button
                      type="button"
                      onClick={() => loadStatement(statementId!, undefined, false)}
                      style={{ background: !statementModeAll ? UK.blue : '#f1f5f9', color: !statementModeAll ? '#fff' : '#334155', border: !statementModeAll ? `1px solid ${UK.blue}` : '1px solid #cbd5e1', borderRadius: 7, padding: '6px 11px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                    >
                      By Property
                    </button>
                    <button
                      type="button"
                      onClick={() => loadStatement(statementId!, undefined, true)}
                      style={{ background: statementModeAll ? UK.blue : '#f1f5f9', color: statementModeAll ? '#fff' : '#334155', border: statementModeAll ? `1px solid ${UK.blue}` : '1px solid #cbd5e1', borderRadius: 7, padding: '6px 11px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                    >
                      All Properties
                    </button>
                  </div>
                  {Array.isArray(statementData.lease_periods) && statementData.lease_periods.length > 0 && !statementModeAll && (
                    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', marginBottom: 16 }}>
                      <div style={{ fontSize: 12, fontWeight: 800, color: '#374151', marginBottom: 8 }}>Statement Periods</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {(() => {
                          const periods = [...statementData.lease_periods].reverse();
                          const total = periods.length;
                          return periods.map((l: any, idx: number) => {
                            const periodNum = total - idx;
                            const leaseState = String(l.lease_status || l.computed_status || '').toLowerCase();
                            const isSelected = selectedHistoricalLeaseId === l.id || (!selectedHistoricalLeaseId && idx === 0 && (leaseState === 'active' || leaseState === 'expiring_soon' || leaseState === 'notice_given'));
                            const badge = leaseState === 'ended' || leaseState === 'expired'
                              ? { bg: '#fee2e2', color: UK.red, label: 'Ended' }
                              : leaseState === 'notice_given'
                                ? { bg: '#fef3c7', color: '#b45309', label: 'Notice' }
                                : { bg: '#dcfce7', color: '#16a34a', label: 'Active' };
                            return (
                              <button
                                key={l.id}
                                type="button"
                                onClick={() => loadStatement(statementId!, l.id, false)}
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '8px 10px', borderRadius: 8, border: isSelected ? `2px solid ${UK.blue}` : '1.5px solid #e5e7eb', background: isSelected ? '#eff6ff' : '#fff', cursor: 'pointer', fontSize: 12, color: '#374151', textAlign: 'left' }}
                              >
                                <span style={{ fontWeight: isSelected ? 700 : 500 }}>
                                  <span style={{ background: '#f1f5f9', color: '#475569', borderRadius: 4, padding: '1px 6px', fontSize: 11, fontWeight: 800, marginRight: 7 }}>Period {periodNum}</span>
                                  {fmt(l.start_date)} → {fmt(l.end_date)} · {l.property_address || '—'}
                                </span>
                                <span style={{ background: badge.bg, color: badge.color, borderRadius: 999, padding: '2px 8px', fontWeight: 700, flexShrink: 0 }}>{badge.label}</span>
                              </button>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  )}
                  {statementModeAll && (
                    <div style={{ background: '#eff6ff', border: `1px solid ${UK.blue}33`, borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 12, fontWeight: 700, color: UK.blue }}>
                      Combined view across all linked properties. Switch to By Property for a clearer per-property breakdown.
                    </div>
                  )}
                  {selectedHistoricalLeaseId && !statementModeAll && (
                    <div style={{ background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 12, fontWeight: 700, color: '#92400e' }}>
                      Viewing selected lease period only.
                    </div>
                  )}
                  <div style={{ overflowX: 'auto', marginBottom: 20 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ background: '#f8fafc' }}>
                          {['Date', 'Description', 'Debit', 'Credit', 'Balance'].map(h => (
                            <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 700, color: '#374151', borderBottom: '2px solid #e5e7eb', fontSize: 12, whiteSpace: 'nowrap' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(statementData.rows || []).map((row: any, i: number) => (
                          <tr key={i} style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                            <td style={{ padding: '9px 12px', color: '#6b7280', whiteSpace: 'nowrap' }}>{ml(row.date)}</td>
                            <td style={{ padding: '9px 12px', color: '#374151' }}>{row.description}</td>
                            <td style={{ padding: '9px 12px', color: UK.red, fontWeight: 600 }}>{row.debit ? `£${Number(row.debit).toFixed(2)}` : '—'}</td>
                            <td style={{ padding: '9px 12px', color: '#16a34a', fontWeight: 600 }}>{row.credit ? `£${Number(row.credit).toFixed(2)}` : '—'}</td>
                            <td style={{ padding: '9px 12px', fontWeight: 700, color: row.balance < 0 ? UK.red : '#16a34a' }}>
                              {row.balance < 0 ? `-£${Math.abs(row.balance).toFixed(2)}` : row.balance === 0 ? '£0.00' : `+£${Number(row.balance).toFixed(2)}`}
                            </td>
                          </tr>
                        ))}
                        {(statementData.rows || []).length === 0 && (
                          <tr><td colSpan={5} style={{ textAlign: 'center', padding: 20, color: '#9ca3af' }}>No rent records found.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div style={{ background: '#f8fafc', borderRadius: 10, padding: '16px 18px', marginBottom: 20, display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, textAlign: 'center' }}>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: 4 }}>Total Charged</div>
                      <div style={{ fontSize: 18, fontWeight: 900, color: '#0f172a' }}>£{Number(statementData.totalCharged || 0).toFixed(2)}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: 4 }}>Total Paid</div>
                      <div style={{ fontSize: 18, fontWeight: 900, color: '#16a34a' }}>£{Number(statementData.totalPaid || 0).toFixed(2)}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: 4 }}>Outstanding</div>
                      <div style={{ fontSize: 18, fontWeight: 900, color: statementData.outstanding > 0 ? UK.red : '#16a34a' }}>
                        {statementData.outstanding > 0 ? `£${Number(statementData.outstanding).toFixed(2)}` : 'Nil'}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button type="button" onClick={() => downloadStatementPDF(statementId!, st?.full_name || 'tenant')} style={{ flex: 1, padding: 10, background: UK.blue, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>Download PDF</button>
                    <button type="button" onClick={() => emailStatement(statementId!)} disabled={emailingStatement} style={{ flex: 1, padding: 10, background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>{emailingStatement ? 'Sending…' : 'Email Tenant'}</button>
                  </div>
                </>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
}
